import {useState} from 'react'
import Button from 'components/ui/Button'
import TextField from 'components/ui/TextField'
import SingleSelect from 'components/ui/Select/SingleSelect'
import TextFieldSkeleton from 'components/ui/Skeletons/TextFieldSkeleton'
import {SelectChangeEvent} from '@mui/material/Select'
import {Link, useNavigate} from 'react-router-dom'
import * as Styles from 'layouts/PrivateLayout.styles'
import {statesSelectOptions, timeZonesSelectOptions} from './constants'
import {locationsApi} from "../../../services/locations"
import {ILocation} from "../../../interfaces/locations"

interface FormErrors {
    [key: string]: string
}

const LocationsAdd = () => {
    const [timeZoneId, setTimeZoneId] = useState('')
    const [stateId, setStateId] = useState('')
    const [errors, setErrors] = useState<FormErrors>({})
    const navigate = useNavigate()

    const [createLocation, {isLoading: isCreating}] = locationsApi.useCreateLocationMutation()

    const validateForm = (formData: any): FormErrors => {
        const errors: FormErrors = {}
        if (!formData.name) errors.name = 'Location name is required'
        if (!formData.address) errors.address = 'Address is required'
        if (!formData.zip) errors.zip = 'ZIP code is required'
        if (formData.zip && !/^\d{5}(-\d{4})?$/.test(formData.zip)) errors.zip = 'Invalid ZIP code'
        if (!formData.city) errors.city = 'City is required'
        if (!formData.state) errors.state = 'State is required'
        if (!formData.time_zone) errors.time_zone = 'Time zone is required'
        return errors
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const formData = new FormData(event.currentTarget)
        const formDataObject: any = Object.fromEntries(formData.entries())

        const formErrors = validateForm(formDataObject)
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors)
            return
        }

        const locationData: Partial<ILocation> = {
            name: formDataObject.name,
            address_1: formDataObject.address,
            address_2: formDataObject.address2 || '',
            city: formDataObject.city,
            state: formDataObject.state,
            zip_code: formDataObject.zip,
            country: 'US',
            timezone: timeZonesSelectOptions.find(
                (option) => option.id === formDataObject.time_zone
            )?.label || "Unknown Time Zone",
            capacity: 1,
            shopify_location_id: null,
        }

        try {
            const response = await createLocation(locationData).unwrap()
            console.log('Location created successfully:', response)
            navigate('/admin/locations')
        } catch (error) {
            console.error('Failed to create location:', error)
            setErrors({submit: 'Failed to create location. Please try again.'})
        }
    }

    return (
        <div>
            <Styles.PageTitleContainer>
                <div>Add new location</div>
            </Styles.PageTitleContainer>

            <form onSubmit={handleSubmit}>
                <Styles.FormColumns border-type="border">
                    <Styles.FormColumnLeft>Location Name*</Styles.FormColumnLeft>
                    <Styles.FormColumnRight>
                        <TextField
                            id="name"
                            required
                            cc-width="full"
                            label="Location name"
                            error={!!errors.name}
                            helperText={errors.name}
                        />
                    </Styles.FormColumnRight>
                </Styles.FormColumns>

                <Styles.FormColumns border-type="border">
                    <Styles.FormColumnLeft>Address*</Styles.FormColumnLeft>
                    <Styles.FormColumnRight>
                        <TextField
                            id="address"
                            label="Address"
                            required
                            cc-width="full"
                            error={!!errors.address}
                            helperText={errors.address}
                        />
                        <TextField id="address2" label="Address 2" cc-width="full"/>
                        <Styles.SectionThreeColumns>
                            <TextField
                                id="zip"
                                label="ZIP"
                                type="text"
                                required
                                cc-width="full"
                                error={!!errors.zip}
                                helperText={errors.zip}
                            />
                            <TextField
                                id="city"
                                label="City"
                                required
                                cc-width="full"
                                error={!!errors.city}
                                helperText={errors.city}
                            />
                            <SingleSelect
                                id="state"
                                label={'State'}
                                value={stateId}
                                onChange={(event: SelectChangeEvent<any>) =>
                                    setStateId(event.target.value)
                                }
                                required
                                options={[
                                    {id: '', label: 'Select State'},
                                    ...statesSelectOptions
                                ]}
                                error={!!errors.state}
                            />
                        </Styles.SectionThreeColumns>
                    </Styles.FormColumnRight>
                </Styles.FormColumns>

                <Styles.FormColumns border-type="border">
                    <Styles.FormColumnLeft>Time Zone*</Styles.FormColumnLeft>
                    <Styles.FormColumnRight>
                        <TextFieldSkeleton isLoading={isCreating}>
                            <SingleSelect
                                id="time_zone"
                                label={'Select time zone'}
                                value={timeZoneId}
                                onChange={(event: SelectChangeEvent<any>) =>
                                    setTimeZoneId(event.target.value)
                                }
                                required
                                options={[
                                    {id: '', label: 'Select Time Zone'},
                                    ...timeZonesSelectOptions
                                ]}
                                error={!!errors.time_zone}
                            />
                        </TextFieldSkeleton>
                    </Styles.FormColumnRight>
                </Styles.FormColumns>

                {/*<Styles.FormColumns border-type="border">*/}
                {/*    <Styles.FormColumnLeft>Capacity</Styles.FormColumnLeft>*/}
                {/*    <Styles.FormColumnRight>*/}
                {/*        <TextField*/}
                {/*            id="capacity"*/}
                {/*            type="number"*/}
                {/*            cc-width="full"*/}
                {/*            error={!!errors.capacity}*/}
                {/*            helperText={errors.capacity}*/}
                {/*        />*/}
                {/*    </Styles.FormColumnRight>*/}
                {/*</Styles.FormColumns>*/}

                {errors.submit && (
                    <Styles.FormColumns>
                        <Styles.FormColumnLeft/>
                        <Styles.FormColumnRight>
                            <div style={{color: 'red'}}>{errors.submit}</div>
                        </Styles.FormColumnRight>
                    </Styles.FormColumns>
                )}

                <Styles.FormColumnsFooter>
                    <Styles.FormColumnLeft/>
                    <Styles.FormColumnRight>
                        <Styles.FormColumnsFooterButtons>
                            <Link to={'/admin/locations'}>
                                <Button>Cancel</Button>
                            </Link>
                            <Button
                                id="addLocationButton"
                                type="submit"
                                loading={isCreating}
                                cc-color="purple"
                            >
                                Save
                            </Button>
                        </Styles.FormColumnsFooterButtons>
                    </Styles.FormColumnRight>
                </Styles.FormColumnsFooter>
            </form>
        </div>
    )
}

export default LocationsAdd
